import clsx from 'clsx';
import IconButton from 'components/IconButton';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { PulseOutline } from 'react-ionicons';
import Divider from 'components/Divider';
import {
  OfferingType,
  ProductCategory,
  TagType,
} from 'screens/AccessExpressV3/accessExpress.types';
import { AccessServiceSummary } from '../AccessServiceSummary';
import DetailsItem from './DetailsItem';
import {
  TermType,
  formatCarrier,
  getServiceActivationFee,
} from 'screens/SelectServices/utils';
import { ProductType } from 'screens/SelectServices/schemas';
import { observer } from 'mobx-react-lite';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { AddToCartButton } from '../AddToCartButton';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { formatCurrency } from 'shared/util/formatCurrency';
import { StarlinkServiceSummary } from '../StarlinkServiceSummary';

const CuratedOfferDetails = observer(
  ({
    tag,
    isSelected,
    onAddToCart,
    offering,
    serviceSummaryData,
    mns,
    onMNSChange,
    graniteInstall,
    onGraniteInstallChange,
    category,
  }: {
    tag: TagType;
    isSelected: boolean;
    onAddToCart: (tag?: string) => void;
    offering: OfferingType;
    serviceSummaryData?: (ProductType & { term: TermType }) | undefined;
    mns: SelectedPackages;
    onMNSChange: () => void;
    graniteInstall?: boolean;
    onGraniteInstallChange?: () => void;
    category: ProductCategory;
  }) => {
    const { hasCircuitLimitReached } = useCartContext();
    const [expand, setExpand] = useState(false);

    const onExpand = () => setExpand((prev) => !prev);

    const serviceActivationFee = getServiceActivationFee(serviceSummaryData);

    const isDisabled = hasCircuitLimitReached('Broadband', offering.id!);

    const hasMNS = mns.essential || mns.enhanced;

    const pricing =
      serviceSummaryData?.formal_broadband_pricing ||
      serviceSummaryData?.formal_dia_pricing;

    const totalMRC =
      (offering.price ?? 0) +
      (mns.essential
        ? pricing?.mns_essentials ?? 0
        : mns.enhanced
          ? pricing?.mns_enhanced ?? 0
          : 0);

    const starlinkPricing = serviceSummaryData?.formal_starlink_pricing;
    const starlinkTotalNRC = starlinkPricing
      ? (starlinkPricing.enterprise_bundle ?? 0) +
        (graniteInstall ? starlinkPricing.installation ?? 0 : 0) +
        (starlinkPricing.activation ?? 0)
      : undefined;

    return (
      <div className="flex w-full flex-wrap items-start justify-between gap-6">
        <div className="flex w-1/2 flex-1 flex-col gap-4">
          <h1
            className={clsx(
              'text-4xl font-bold text-content-base-default',
              tag !== 'Best offer' && '!text-2xl',
            )}
          >
            {offering.carrier && formatCarrier(offering.carrier)}
          </h1>
          <p className="flex flex-col justify-center font-bold">
            <span
              className="group flex h-[38px] w-fit cursor-pointer items-center gap-1"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onExpand();
              }}
            >
              <span className="text-[28px] text-content-base-default">
                {formatCurrency(totalMRC)}
              </span>
              <span className="self-center text-xl text-content-base-subdued">
                /month
              </span>
              <IconButton
                icon={'chevronDown'}
                className={clsx(
                  expand && 'rotate-180',
                  'group-hover:text-content-accent-default',
                )}
                buttonType="ghost"
                color="currentColor"
              />
            </span>
            <span className="text-xs">
              {category === 'Starlink'
                ? `+${formatCurrency(starlinkTotalNRC)} in one-time fees*`
                : `+${serviceActivationFee} service activation fee`}
            </span>
            <motion.div
              className="overflow-hidden"
              initial={{ height: 0, opacity: 0, marginTop: 0 }}
              animate={{
                opacity: expand ? 1 : 0,
                height: expand ? 'auto' : 0,
                overflow: 'hidden',
                transitionEnd: {
                  overflow: expand ? 'visible' : 'hidden',
                },
              }}
              transition={{
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
            >
              {category !== 'Starlink' ? (
                <>
                  <Divider className="my-4" />
                  <AccessServiceSummary data={serviceSummaryData} mns={mns} />
                  <GraniteButton
                    variant={hasMNS ? 'secondary' : 'primary'}
                    className="mt-6 w-full max-w-[256px] whitespace-nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMNSChange();
                    }}
                  >
                    {hasMNS
                      ? 'Remove network monitoring'
                      : 'Add network monitoring'}
                  </GraniteButton>
                </>
              ) : (
                <>
                  <StarlinkServiceSummary
                    data={serviceSummaryData}
                    graniteInstall={graniteInstall}
                  />
                  <Divider className="my-2" />
                  <span
                    className={clsx(
                      'mb-[20px] block text-xs text-content-base-subdued',
                      !graniteInstall && 'invisible',
                    )}
                  >
                    Install fee subject to change*
                  </span>

                  {category === 'Starlink' && expand && (
                    <GraniteButton
                      variant={graniteInstall ? 'secondary' : 'primary'}
                      className={clsx(
                        'my-2 w-auto md:whitespace-nowrap',
                        !graniteInstall ? 'max-w-[297px]' : 'max-w-[268px]',
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onGraniteInstallChange?.();
                      }}
                    >
                      {graniteInstall
                        ? 'Remove Granite install add-on'
                        : 'Add Granite install for $750.00 NRC'}
                    </GraniteButton>
                  )}
                </>
              )}
            </motion.div>
          </p>
          <AddToCartButton
            isSelected={isSelected}
            onAddToCart={onAddToCart}
            tag={tag}
            className={clsx(
              'hidden xl:flex',
              category === 'Starlink' &&
                tag !== 'Best offer' &&
                '!max-w-[168px]',
            )}
            disabled={isDisabled}
          />
        </div>
        <div
          className={clsx(
            'flex w-full flex-1 flex-wrap gap-x-2 gap-y-0',
            (tag === 'Best offer' || category === 'Starlink') &&
              'xs:min-w-[200px] xs:max-w-[390px] xl:mt-7',
          )}
        >
          {offering.details?.map((item, index) => {
            const showConstructionWarning =
              offering.construction_likely && item.id === 'construction_likely';

            if (item.id === 'installation' && !graniteInstall) return null;

            return (
              <div
                key={index}
                className={clsx(
                  'flex-[1_0_calc(33.333%-8px)]',
                  !['speed', 'term'].includes(item.id!) && 'min-w-[160px] ',
                )}
              >
                <DetailsItem
                  {...item}
                  icon={item.icon as React.ElementType}
                  showConstructionWarning={showConstructionWarning}
                />
              </div>
            );
          })}

          {category !== 'Starlink' && hasMNS && (
            <div className="flex-[1_0_calc(33.333%-8px)]">
              <DetailsItem icon={PulseOutline} text="Network monitoring" />
            </div>
          )}
        </div>
        <AddToCartButton
          isSelected={isSelected}
          onAddToCart={onAddToCart}
          tag={tag}
          className="mt-auto block h-min xl:hidden"
          disabled={isDisabled}
        />
      </div>
    );
  },
);

export default CuratedOfferDetails;
